import className from "classnames/bind";
import { RefObject, useEffect, useRef } from "react";
import { useHeader } from "@/providers/HeaderContext";
import gsap from "gsap";

import styles from "./Tabs.module.scss";

const cx = className.bind(styles);

interface TabsProps {
  aZLabel: string;
  byClassLabel: string;
  activeTab: "a-z" | "by-class";
  isFixed: boolean;
  isVisible?: boolean;
  onTabClick?: (tab: "a-z" | "by-class") => void;
  elRef?: RefObject<HTMLDivElement>;
}

export default function Tabs({
  aZLabel,
  byClassLabel,
  activeTab,
  isFixed,
  isVisible,
  onTabClick,
  elRef,
}: TabsProps) {
  const aZTabEl = useRef<HTMLButtonElement>(null);
  const tabRootEl = useRef<HTMLDivElement>(null);
  const byClassTabEl = useRef<HTMLButtonElement>(null);
  const tabBackgroundEl = useRef<HTMLSpanElement>(null);

  const {
    isScrolled: isHeaderScrolled,
    scrollDirection: headerScrollDirection,
  } = useHeader();

  useEffect(() => {
    if (!isFixed) {
      const tween = gsap.fromTo(
        tabRootEl.current,
        { opacity: 0 },
        { opacity: 1 }
      );

      return () => {
        tween?.kill();
      };
    }
  }, [isFixed]);

  useEffect(() => {
    if (elRef) {
      elRef.current = tabRootEl.current;
    }
  }, [elRef, tabRootEl]);

  // Reset tab controls styles when activeTab changes
  useEffect(() => {
    if (!isFixed && tabRootEl.current) {
      // Clear any inline styles that might be lingering
      gsap.set(tabRootEl.current, { clearProps: "all" });

      // Ensure tab is visible
      gsap.set(tabRootEl.current, { opacity: 1 });
    }
  }, [activeTab, isFixed]);

  return (
    <div
      className={cx("tab-controls", {
        "is-fixed": isFixed,
        "is-scrolled":
          activeTab === "by-class" ||
          (isHeaderScrolled && headerScrollDirection === "up"),
        "is-visible": isVisible,
      })}
      ref={tabRootEl}
    >
      <button
        role={"tab"}
        aria-selected={activeTab === "a-z"}
        className={cx([
          "tab-button",
          "eyebrow-l",
          { active: activeTab === "a-z" },
        ])}
        ref={aZTabEl}
        id="inductees-tab"
        tabIndex={isVisible ? 0 : -1}
        onClick={() => onTabClick("a-z")}
      >
        {aZLabel}
      </button>
      <button
        role={"tab"}
        aria-selected={activeTab === "by-class"}
        className={cx([
          "tab-button",
          "eyebrow-l",
          { active: activeTab === "by-class" },
        ])}
        tabIndex={isVisible ? 0 : -1}
        ref={byClassTabEl}
        onClick={() => onTabClick("by-class")}
      >
        {byClassLabel}
      </button>
      <span
        className={cx("background", { [activeTab]: true })}
        ref={tabBackgroundEl}
      />
    </div>
  );
}
